import "./contextMenuItems.js";

import { Disposable } from "../../../base/common/lifecycle.js";
import { CommandsRegistry } from "../../commands/common/commands.js";
import { isHierarchyNode } from "../../hierarchy/common/hierarchy.js";
import { ILogService } from "../../log/common/log.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";
import { GetPropertyOptions, IProperty, IPropertyCollection, IPropertyProvider, IPropertyService, IPropertyStateProvider, isPropertyLike, PropertyAddress, PropertyChangedEvent, PropertyCollection, PropertyScope, PropertyViewMode } from "./properties.js";
import { CopyPropertyGroupCommand, SetPropertyCommand } from "./propertyCommands.js";
import { Emitter } from "../../../base/common/events.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";



export class PropertyService extends Disposable implements IPropertyService {
    readonly _serviceBrand: undefined;

    private readonly _onPropertyChanged = this._register(new Emitter<PropertyChangedEvent>());
    readonly propertyChanged = this._onPropertyChanged.event;

    private readonly _providers: IPropertyProvider[] = [];

    constructor(
        @ILogService private readonly logger: ILogService,
        @IStorageService private readonly storageService: IStorageService,
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
    ) {
        super();
        this._register(CommandsRegistry.registerCommand(new SetPropertyCommand()));
        this._register(CommandsRegistry.registerCommand(new CopyPropertyGroupCommand()));
    }

    registerProvider(provider: IPropertyProvider): void {
        if (this._providers.includes(provider)) {
            this.logger.debug("PropertyService: Provider already registered.", provider);
            return;
        }
        this.logger.debug("PropertyService: Registering provider");
        this._providers.push(provider);
    }

    unregisterProvider(provider: IPropertyProvider): void {
        const index = this._providers.indexOf(provider);
        if (index >= 0) {
            this.logger.debug("PropertyService: Unregistering provider");
            this._providers.splice(index, 1);
        }
    }


    private readonly _inflight: WeakMap<object, Promise<IPropertyCollection>> = new WeakMap();
    // private readonly _properties: WeakMap<object, IPropertyCollection> = new WeakMap();

    async getProperties(object: object, options: GetPropertyOptions): Promise<IPropertyCollection> {
        if (!object) {
            return PropertyCollection.Empty;
        }

        if (isPropertyLike(object)) {
            object = object.owner;
        }
        else if (isHierarchyNode(object)) {
            object = object.owner;
        }

        const active = this._inflight.get(object);
        if (active) {
            this.logger.debug("PropertyService: Detected possible infinite recursion in getProperties call stack.");
            return active;
        }

        // @TODO: we need to serialize the options too to be able to dispose
        // and then we put the pressure on disposing on this service but it should be done by whoever requests properties
        // We should not put more burden on the propertyservice i think (disposing requested collections)
        // if (this._properties.has(object)) {
        //     const prev = this._properties.get(object)!;
        //     prev.dispose();
        //     this._properties.delete(object);
        // }

        const fn: () => Promise<IPropertyCollection> = async () => {

            // const stateProvider = this._stateProviders[options.scope === PropertyScope.AI_QUERY ? "ai" : "default"];

            const col = new PropertyCollection();
            for (const provider of this._providers) {
                if (!provider.canHandle(object)) {
                    continue;
                }
                const providerProps = await provider.getProperties(object, {
                    ...options
                });
                if (providerProps) {
                    if (providerProps.name?.length && (!col.name || col.name.length < 3)) {
                        col.name = providerProps.name;
                    }
                    for (const group of providerProps.groups) {
                        col.groups.push(group);
                    }
                }
            }
            if (!col.name?.length || col.name.length <= 2) {
                col.name = object.constructor?.name.length > 3 ? object.constructor.name : "";
                if ("name" in object && typeof object.name === "string") {
                    col.name = object.name;
                }
            }

            // Sort groups by priority
            for (const group of col.groups) {
                // console.log(group.properties)
                group.properties.sort((a, b) => {
                    const pa = a.metadata?.priority || 0;
                    const pb = b.metadata?.priority || 0;
                    return pb - pa;
                });
            }

            if (options.scope === PropertyScope.INSPECTOR) {
                col.persistFrom(this.storageService);
            }

            // Hook up property change events and re-emit from service
            for (const group of col.groups) {
                for (const prop of group.properties) {
                    prop.onDidChange(e => this._onPropertyChanged?.fire(e));
                }
            }


            col.watch(this.contextKeyService, this.logger);

            return col;

        }
        const promise = fn().then(col => {
            // this._properties.set(object, col);
            return col;
        });
        this._inflight.set(object, promise);
        promise.finally(() => {
            this._inflight.delete(object);
        });
        return promise;
    }



    async findProperty(address: PropertyAddress): Promise<IProperty | null> {
        this.logger.debug("PropertyService: Finding property", address);
        for (const provider of this._providers) {
            try {
                const property = await provider.findProperty(address);
                if (property) {
                    return property;
                }
            }
            catch (e) {
                this.logger.error("PropertyService: Error finding property from provider", e, provider, address);
            }
        }
        return null;
    }


}


export class PropertyStateProviderGroup implements IPropertyStateProvider {

    private readonly providers: IPropertyStateProvider[] = [];

    constructor(
        ...providers: Array<IPropertyStateProvider | null | undefined>
    ) {
        this.providers = providers.filter(p => p != null) as IPropertyStateProvider[];
    }

    isHidden(object: object, key: string): boolean {
        for (const provider of this.providers) {
            if (provider.isHidden(object, key)) {
                return true;
            }
        }
        return false;
    }

    setHidden(object: object, key: string, hidden: boolean): void {
        for (const provider of this.providers) {
            provider.setHidden?.(object, key, hidden);
        }
    }
}


export class BasePropertyStateProvider extends Disposable implements IPropertyStateProvider {

    constructor(
        protected readonly storageService: IStorageService,
    ) {
        super();
    }

    isHidden(object: object, key: string): boolean {
        const storageKey = `property.${this.tryGetPropertyStorageKey(object, key)}.hidden`;
        return this.storageService.getBoolean(storageKey, StorageScope.APPLICATION, false);
    }
    setHidden(object: object, key: string, hidden: boolean): void {
        const storageKey = `property.${this.tryGetPropertyStorageKey(object, key)}.hidden`;
        this.storageService.store(storageKey, hidden, StorageScope.APPLICATION, StorageTarget.MACHINE);
    }

    protected tryGetPropertyStorageKey(object: object, propertyKey: string) {
        let objectId: string | null = null;
        if ("type" in object) {
            objectId = (object as any).type;
        }
        else if (object.constructor && object.constructor.name) {
            objectId = object.constructor.name;
        }
        else {
            objectId = "object";
        }
        return `${objectId}.${propertyKey}`;
    }
}

export class ViewModePropertyStateProvider extends BasePropertyStateProvider implements IPropertyStateProvider {

    constructor(
        private readonly viewMode: PropertyViewMode,
        storageService: IStorageService,
    ) {
        super(storageService);
    }

    protected tryGetPropertyStorageKey(object: object, propertyKey: string): string {
        let key = super.tryGetPropertyStorageKey(object, propertyKey);
        const viewModeName = PropertyViewMode[this.viewMode];
        key = `${key}.viewmode.${viewModeName}`;
        return key;
    }
}